import { Button } from '@stormwater-software/lib-ui';
import React, { useContext, useEffect, useRef, useState } from 'react';
import projectApi from '../../../../utils/api/project';
import { ProjectContext } from '../../../../contexts/ProjectContext';
import Pagination from 'react-js-pagination';
import _ from 'lodash';
import Loading from '../../../Shared/ResourceIndex/Loading';
import ReactTooltip from 'react-tooltip';
import { formatInTimeZone } from 'date-fns-tz';

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const PublicVisitReport: React.FC<any> = () => {
  const widgetRef = useRef<any>(0);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [publicVisitReportData, setPublicVisitReportData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const projectContext = useContext(ProjectContext);
  const projectId =
    projectContext && projectContext.project && projectContext.project.id;

  useEffect(() => {
    getPublicVisitReport();
  }, [projectId, page, rowsPerPage, search]);

  const dateDisplayFunc = (ele: any) => {
    if (!ele) return 'NA';
    return formatInTimeZone(ele, userTimeZone, 'MM/dd/yyyy hh:mm a');
  };

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds === 0) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    let result = '';
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}m `;
    if (secs > 0 || result === '') result += `${secs}s`;

    return result.trim();
  };

  function getPublicVisitReport() {
    setIsLoading(true);
    const data = {
      p: page,
      rows: rowsPerPage,
      search: search,
    };

    projectApi
      .getPublicVisitReport(projectId, data)
      .then((res) => {
        setPublicVisitReportData(res.data || []);
        setCount(res.count || 0);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching public visit report:', error);
        setPublicVisitReportData([]);
        setCount(0);
        setIsLoading(false);
      });
  }

  const onSearchChangeHandler = _.debounce((event: any) => {
    setSearch(event.target.value);
    setPage(1);
  }, 500);

  const handleDownloadCSV = () => {
    const data = {
      p: 1,
      rows: count,
      search: search,
    };

    projectApi.getPublicVisitReport(projectId, data).then((res) => {
      const csvData = res.data || [];

      // Create CSV content
      const headers = [
        'Name',
        'Email',
        'Company',
        'First Seen',
        'Last Seen',
        'Visit Duration',
      ];
      const csvRows = [headers.join(',')];

      csvData.forEach((ele: any) => {
        const row = [
          `"${ele.name || 'NA'}"`,
          `"${ele.email || 'NA'}"`,
          `"${ele.company || 'NA'}"`,
          `"${dateDisplayFunc(ele.first_seen)}"`,
          `"${dateDisplayFunc(ele.last_seen)}"`,
          `"${formatDuration(ele.visit_duration || 0)}"`,
        ];
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${
        projectContext.project?.name
      }_Public_Visit_Report_${new Date()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  };

  return (
    <div className='public-visit-report'>
      <header>
        <h2 className='public-visit-report__header-name'>
          Public Visit Session Report
        </h2>
        <div className='public-visit-report__header-tools'>
          <input
            onChange={onSearchChangeHandler}
            placeholder='Search by name, email or company'
          />
          <Button
            className='public-visit-report__download-btn'
            onClick={handleDownloadCSV}
          >
            Download
          </Button>
        </div>
      </header>
      <main>
        <>
          <div className='public-visit-report-table' ref={widgetRef}>
            <div className='public-visit-report-table__body'>
              <div className='public-visit-report-table__content'>
                <section className='public-visit-report-table__list-view'>
                  <div className='public-visit-report-table__table-head'>
                    <h3>Name</h3>
                    <h3>Email</h3>
                    <h3>Company</h3>
                    <h3>First Seen</h3>
                    <h3>Last Seen</h3>
                    <h3>Visit Duration</h3>
                  </div>

                  {isLoading ? (
                    <Loading what={'Loading'} />
                  ) : publicVisitReportData &&
                    publicVisitReportData.length > 0 ? (
                    publicVisitReportData.map((ele: any, index: any) => {
                      return (
                        <div
                          key={index}
                          className='public-visit-report-table__table-body'
                        >
                          <li data-tip={`${ele.name || 'NA'}`}>
                            <ReactTooltip
                              place='top'
                              type='dark'
                              effect='float'
                            />
                            {ele.name || 'NA'}
                          </li>
                          <li data-tip={`${ele.email || 'NA'}`}>
                            <ReactTooltip
                              place='top'
                              type='dark'
                              effect='float'
                            />
                            {ele.email || 'NA'}
                          </li>
                          <li data-tip={`${ele.company || 'NA'}`}>
                            <ReactTooltip
                              place='top'
                              type='dark'
                              effect='float'
                            />
                            {ele.company || 'NA'}
                          </li>
                          <li style={{ textAlign: 'center' }}>
                            {dateDisplayFunc(ele.first_seen)}
                          </li>
                          <li style={{ textAlign: 'center' }}>
                            {dateDisplayFunc(ele.last_seen)}
                          </li>
                          <li style={{ textAlign: 'center' }}>
                            {formatDuration(ele.visit_duration || 0)}
                          </li>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      No Public Visit Data
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        </>
        <div className='public-visit-report__pagination-wrapper'>
          <div>
            <span>Rows Per Page : </span>
            <select
              className='public-visit-report__pagination-select'
              onChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(1);
              }}
            >
              <option value='10'>10</option>
              <option value='20'>20</option>
              <option value='30'>30</option>
              <option value='40'>40</option>
              <option value='50'>50</option>
            </select>
          </div>
          <Pagination
            activePage={page}
            itemsCountPerPage={rowsPerPage}
            totalItemsCount={Number(count)}
            pageRangeDisplayed={3}
            onChange={(currentPage) => {
              setPage(currentPage);
            }}
            itemClass='pagination-item'
          />
        </div>
      </main>
    </div>
  );
};

export default PublicVisitReport;
